import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import PremiumBanner from "../components/PremiumBanner";
import HomeShortcuts from "../components/HomeShortcuts";
import {
  COMPOSER_MODES,
  DEV_EMPTY_MODE,
  DEFAULT_COMPOSER_MODE,
  SYMBOL_GROUPS,
} from "../components/composerConfig";
import { createPortal } from "react-dom";
import {
  Loader,
  ArrowUp,
  FileText,
  Upload,
  X,
  ChevronDown,
  Check,
  Star,
  Lock,
  Play,
  ChevronLeft,
  ChevronRight,
  Volume2,
  VolumeX,
  Sparkles,
  Instagram,
  ExternalLink,
  Eye,
  Heart,
} from "lucide-react";
import { apiService } from "../apiService";
import OnboardingBubbles from "../components/OnboardingBubbles";
import VideoThumbnail from "../components/VideoThumbnail";
import { trackFunnelEvent } from "../utils/ev";
import { GENERIC_ERROR_MESSAGE, busyMessageFor, busyToneFor,
         getDefaultLanguage } from "../utils/constants";
import { demoVideoSrc, demoPosterSrc } from "../utils/demoSrc";
import { useDemoReel, REEL_CROSSFADE_MS } from "../hooks/useDemoReel";
// Shared with App.jsx, which mounts the persistent <video> and needs the FIRST of
// these to start on. See the note in that module for why it is not declared here.
import { DEMO_VIDEO_IDS, DEMO_VIDEOS } from "../config/demoVideos";
import { preloadThumbnails } from "../utils/thumbnailPreloader";
import VideoPlayer from "../components/VideoPlayer";
import GatedVideoPlayer from "../components/GatedVideoPlayer";
import SubscriptionModal from "../components/SubscriptionModal";
import { setPendingUpload } from "../projects/pendingUpload";
import { updateHomeworkSession } from "../homework/homeworkApi";
import { updateLectureSession } from "../lecture/lectureApi";

import { useAuth } from "../AuthContext";
import { useLoginGate } from "../components/LoginGateProvider";
import { useT } from "../i18n/useT";
import { useIsPhone } from "../hooks/useIsPhone";
import { leaveComposerAt } from "../utils/composerHandoff";
import { afterViewportSettles, reanchorFixedChrome } from "../utils/reanchorFixed";
import { arriveAtTop } from "../utils/arrival";
import {
  ROW_TITLE_FONT,
  safeRenderLatex,
  CLAMP_2_LINES,
  TITLE_SCRIM,
} from "../utils/videoTitle";

// Inject fall keyframes (stadium wave: nudge/lean then sweep off)
const FALL_STYLE_ID = 'popular-fall-anims';
if (typeof document !== 'undefined' && !document.getElementById(FALL_STYLE_ID)) {
  const s = document.createElement('style');
  s.id = FALL_STYLE_ID;
  s.textContent = `
    @keyframes popular-fall {
      0%   { transform: translateY(0); opacity: 1; }
      80%  { opacity: 1; }
      100% { transform: translateY(200px); opacity: 0; pointer-events: none; }
    }
    @keyframes popular-label-fall {
      0%   { transform: translateY(0); opacity: 1; }
      70%  { opacity: 1; }
      100% { transform: translateY(60px); opacity: 0; }
    }
  `;
  document.head.appendChild(s);
}

// Modes come from the shared composer config so this picker and the Workspace minibar
// cannot drift — they are the same composer in two shapes, and Home was still offering a
// legacy "Fast" plus a "Fast (3.7)" / "Beats (3.7)" trial pair while Workspace had already
// settled on one "Fast" backed by mode_fast_37. See composerConfig.js.
const MODE_OPTIONS = COMPOSER_MODES;
const DEFAULT_MODE = DEFAULT_COMPOSER_MODE;

const EXAMPLE_TOPICS = [
  "Chaos theory",
  "What eigenvalues mean",
  "How GPS uses relativity",
  "Why 0.999... equals 1",
];

const EAGER_POPULAR_THUMBNAILS = 6;
const PRELOAD_POPULAR_THUMBNAILS = 18;

let cachedDemoMuted = true;

// Instagram reels — real covers + real stats from Instagram Insights
const INSTAGRAM_REELS = [
  {
    id: "reel-waves",
    title: "Sound frequency visualization",
    thumbnail: "/instagram/reel-waves.svg",
    instagram_url: "https://www.instagram.com/p/DV4ieYOjdow/",
    instagram_views: 5300000,
    instagram_likes: 391000,
  },
  {
    id: "reel-negative-iq",
    title: "One person in history had a negative IQ",
    thumbnail: "/instagram/reel-negative-iq.svg",
    instagram_url: "https://www.instagram.com/p/DX5SaCBPVEK/",
    instagram_views: 1900000,
    instagram_likes: 103000,
  },
  {
    id: "reel-continuity",
    title: "The continuity equation",
    thumbnail: "/instagram/reel-continuity.svg",
    instagram_url: "https://www.instagram.com/p/DV7D3y7Dbb_/",
    instagram_views: 622000,
    instagram_likes: 42400,
  },
];

/** One Instagram cover — the picture, its gradient and its stat line.
 *
 *  Split out when the home page stopped having a single arrangement for these: phones get a
 *  stacked column and md upward gets a fan, and the two differ ONLY in where each card sits.
 *  Everything inside the card is identical, so it lives here once. `className` and `style`
 *  carry the geometry; nothing in here knows which arrangement it is in. */
const ReelCard = ({ item, className = '', style }) => {
  const fmt = (n) => {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
    return String(n);
  };
  return (
    <div>
      <div
        className={`overflow-hidden shadow-2xl relative ${className}`}
        style={{ aspectRatio: '4 / 5', ...style }}
      >
        <img
          src={item.thumbnail}
          alt={item.title}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        {/* Gradient overlay */}
        <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/70 to-transparent" />
        {/* Stats */}
        <div className="absolute bottom-0 left-0 right-0 px-4 pb-4 flex items-center gap-3">
          <span className="flex items-center gap-1.5 text-white text-sm font-semibold drop-shadow-md">
            <Eye className="w-4 h-4" />
            {fmt(item.instagram_views)}
          </span>
          <span className="flex items-center gap-1.5 text-white text-sm font-semibold drop-shadow-md">
            <Heart className="w-4 h-4" />
            {fmt(item.instagram_likes)}
          </span>
        </div>
      </div>
    </div>
  );
};


// Fade-in on scroll wrapper
const FadeInSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.1 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"} ${className}`}
    >
      {children}
    </div>
  );
};


// Model picker. Same surface, border, shadow and blue accent as the rest of the app; the
// only additions over the old dropdown are a one-line description under each name (so
// "Fast" and "Premium" mean something without a tooltip) and the subscriber lock.
const ModeDropdown = ({ value, onChange, disabled, isSubscriber, onLocked, t }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const selected = MODE_OPTIONS.find((opt) => opt.value === value) || MODE_OPTIONS[0];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Close on Escape — the menu traps attention, so it needs a keyboard way out.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => e.key === "Escape" && setIsOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium
          text-gray-500 transition-colors cursor-pointer
          disabled:opacity-50 disabled:cursor-not-allowed
          ${isOpen ? "bg-gray-100" : "hover:bg-gray-100"}`}
      >
        {selected.label || t(selected.labelKey)}
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div
          role="listbox"
          className="absolute top-full right-0 mt-2 min-w-[200px] bg-white rounded-xl
            shadow-lg border border-gray-200 p-1 z-50
            animate-in fade-in slide-in-from-top-2 duration-150"
        >
          {MODE_OPTIONS.map((opt) => {
            const locked = opt.subscriberOnly && !isSubscriber;
            const active = value === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => {
                  setIsOpen(false);
                  if (locked) {
                    onLocked?.();
                    return;
                  }
                  onChange(opt.value);
                }}
                /* One row per mode, icon + name — the same shape the Workspace minibar
                   uses. The per-row descriptions went with the trial entries: they existed
                   to tell "Fast" from "Fast (3.7)" from "Beats (3.7)", and with one Fast
                   left they only made this menu twice as tall as the identical one docked
                   on the next page. */
                className={`w-full flex items-center gap-2 text-left px-3 py-2 rounded-lg
                  transition-colors cursor-pointer ${active ? "bg-gray-50" : "hover:bg-gray-50"}`}
              >
                {opt.Icon && (
                  <opt.Icon
                    className={`w-3.5 h-3.5 flex-shrink-0 ${locked ? "text-gray-300" : "text-gray-400"}`}
                  />
                )}
                <span
                  className={`text-sm ${
                    locked ? "text-gray-400" : active ? "text-gray-900 font-medium" : "text-gray-500"
                  }`}
                >
                  {opt.label || t(opt.labelKey)}
                </span>
                {locked && <Lock className="w-3 h-3 text-gray-300 flex-shrink-0" />}
                {active && !locked && (
                  <Check className="w-4 h-4 text-blue-500 flex-shrink-0 ml-auto" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const HomePage = ({
  user,
  credits,
  hasCredits,
  isOutOfCredits,
  creditsLoading,
  generateUnified,
  generateUnifiedBatch,
  solvePdfProblems,
  isConnected,
  generationQueue,
  hidden,
  dailyUsage,
  refreshDailyUsage,
}) => {
  const { userData, authLoading } = useAuth();
  const t = useT();
  const { openLoginGate } = useLoginGate();

  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    text: "",
    pdfFile: null,
    format: "default",
    mode: DEFAULT_MODE,
  });

  const [error, setError] = useState("");
  // 'error' draws the red box, 'info' the blue one. A full queue is not a failure — see
  // busyToneFor. Reset alongside `error` everywhere, so a stale tone cannot outlive its text.
  const [errorTone, setErrorTone] = useState("error");
  const [isQueued, setIsQueued] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const cardStackRef = useRef(null);
  const [anonUsage, setAnonUsage] = useState(null); // {used, remaining, daily_limit}
  const [sampleVideos, setSampleVideos] = useState(DEMO_VIDEOS);
  const [activeDemo, setActiveDemo] = useState(0);
  const [typedText, setTypedText] = useState("");
  const [typePhase, setTypePhase] = useState("typing"); // typing | pausing | erasing | holding
  const [demoActive, setDemoActive] = useState(true);
  const [playingDemoId, setPlayingDemoId] = useState(null);
  const [overrideTitle, setOverrideTitle] = useState(null); // pinned title from unmute or thumbnail click
  const [demoMuted, setDemoMuted] = useState(cachedDemoMuted);
  const [demoVideoReadyForCycle, setDemoVideoReadyForCycle] = useState(false);
  const demoPlayerRef = useRef(null);
  const demoMutedRef = useRef(cachedDemoMuted);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  // Math symbol palette, same one the Workspace composer carries.
  // A phone taps a card to WATCH it; the inline demo below is a desktop affordance.
  const isPhone = useIsPhone();
  const [symbolsOpen, setSymbolsOpen] = useState(false);
  const symbolsRef = useRef(null);
  const symbolsBtnRef = useRef(null);
  const scrollRowRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  // Always false on load: the page should open with the preview playing beside the
  // prompt. Typing expands the form (see onChange), which is when collapsing the
  // player is actually wanted. Seeding from `!!user` collapsed it for every signed-in
  // visitor, so returning users never saw the player at all.
  const [expandedForm, setExpandedForm] = useState(false);
  const [lightboxVideo, setLightboxVideo] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [, setThumbnailPreloadVersion] = useState(0);

  // Home no longer swaps to a generating view: submitting a prompt used to collapse the
  // popular row, the shortcut grid and the player, and replace them with a row of in-progress
  // cards plus a toggle to get back. Popular lessons now simply stay put. Progress lives on
  // the Workspace, where the job's own card carries a dial.
  const isGeneratingMode = false;

  const waitIntervalRef = useRef(null);
  const waitStartTimeRef = useRef(null);
  const hasTrackedFirstVideoRef = useRef(false);
  const hasCompletedFirstVideoRef = useRef(false);

  const [copied, setCopied] = useState(false);

  // Inline video player state
  const [inlineVideo, setInlineVideo] = useState(null); // { videoId, title } or null
  const inlinePlayerRef = useRef(null);
  const inlineFollowUpSending = useRef(false);

  // Kept only as a no-op so the popular row's fall animation stays disabled; the row is
  // never hidden now, so nothing ever plays it.
  const hasPlayedFallRef = useRef(false);

  // Close inline player when navigating away from Home (stops audio)
  useEffect(() => {
    if (hidden && inlineVideo) {
      setInlineVideo(null);
      inlineFollowUpSending.current = false;
    }
  }, [hidden]);


  // Landing page data
  const [landingStats, setLandingStats] = useState(null);
  const [featuredReels, setFeaturedReels] = useState([]);
  useEffect(() => {
    apiService.getLandingStats().then(setLandingStats).catch(() => {});
    apiService.getFeaturedReels()
      .then((r) => setFeaturedReels(r.reels || []))
      .catch(() => {});
  }, []);

  // Enrich demo videos with thumbnail_storage_path so they use direct GCS URLs
  useEffect(() => {
    Promise.all(
      DEMO_VIDEO_IDS.map((d) =>
        fetch(`${apiService.baseUrl}/api/media/video/${d.id}/meta`)
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
      )
    ).then((metas) => {
      setSampleVideos((prev) =>
        prev.map((v, i) => {
          const meta = metas[i];
          if (!meta) return v;
          /* BOTH PATHS, FROM THE ONE REQUEST. The thumbnail path was already being kept;
             `storage_path` was in the same response and thrown away, and it is what lets
             the preview skip the API redirect — see `demoVideoSrc` in utils/demoSrc. */
          const next = { ...v };
          if (meta.thumbnail_storage_path) next.thumbnail_storage_path = meta.thumbnail_storage_path;
          if (meta.storage_path) next.storage_path = meta.storage_path;
          return next;
        })
      );
    });
  }, []);


  // Fetch anonymous usage when user is anonymous (free logged-in users use dailyUsage from useCredits)
  useEffect(() => {
    setAnonUsage(null);
  }, [user]);

  const isSubscriber =
    credits?.subscription_plan && credits.subscription_plan !== "free";

  const detectedLanguage = userData?.preferences?.language
    || getDefaultLanguage(userData?.country_code);

  // Track scroll position for Netflix arrows
  const updateScrollArrows = useCallback(() => {
    const el = scrollRowRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 10);
  }, []);

  const scrollRow = (direction) => {
    const el = scrollRowRef.current;
    if (!el) return;
    if (direction === "left") {
      // Scroll left by whichever is smaller: 80% of view or current scroll position (clamp to 0)
      const amount = Math.min(el.clientWidth * 0.8, el.scrollLeft);
      el.scrollBy({ left: -amount, behavior: "smooth" });
    } else {
      const amount = el.clientWidth * 0.8;
      el.scrollBy({ left: amount, behavior: "smooth" });
    }
  };

  // Just the curated ten. This used to append the ENTIRE explore_featured catalogue behind
  // an infinite-scroll sentinel — 66 cards in a row labelled "Popular lessons", which is not
  // a shortlist of anything. The catalogue still has its own home on Explore.
  const popularLessonVideos = sampleVideos;

  useEffect(() => {
    if (popularLessonVideos.length === 0) return;

    let cancelled = false;
    let idleId = null;
    let timeoutId = null;

    const preloadOffscreenThumbnails = () => {
      const offscreenItems = popularLessonVideos.slice(
        EAGER_POPULAR_THUMBNAILS,
        PRELOAD_POPULAR_THUMBNAILS,
      );
      if (offscreenItems.length === 0) return;

      preloadThumbnails(offscreenItems, {
        priority: "low",
        concurrency: 4,
      })
        .catch(console.warn)
        .finally(() => {
          if (!cancelled) {
            setThumbnailPreloadVersion((version) => version + 1);
          }
        });
    };

    requestAnimationFrame(updateScrollArrows);

    if ("requestIdleCallback" in window) {
      idleId = window.requestIdleCallback(preloadOffscreenThumbnails, {
        timeout: 1200,
      });
    } else {
      timeoutId = window.setTimeout(preloadOffscreenThumbnails, 250);
    }

    return () => {
      cancelled = true;
      if (idleId !== null && "cancelIdleCallback" in window) {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [popularLessonVideos, updateScrollArrows]);

  // Typewriter effect — cycles through video titles
  useEffect(() => {
    if (hidden || !demoActive || sampleVideos.length === 0 || formData.text)
      return;

    // Use overrideTitle (from thumbnail click / unmute) or current demo video title
    const video = sampleVideos[activeDemo];
    const title =
      overrideTitle || (video?.topic || video?.title || "").slice(0, 60);
    if (!title) return;

    // Holding phase — title stays fully displayed, no animation
    if (typePhase === "holding") return;

    if (typePhase === "typing") {
      if (typedText.length < title.length) {
        const timer = setTimeout(
          () => setTypedText(title.slice(0, typedText.length + 1)),
          50,
        );
        return () => clearTimeout(timer);
      } else {
        // If unmuted, hold the title instead of erasing
        if (!demoMutedRef.current) {
          setTypePhase("holding");
          return;
        }
        /* THE HOLD WAITS FOR A PICTURE, BUT NOT FOREVER. Holding the finished title until
           the clip has a frame is what keeps the words and the video in step. Returning
           outright, which is what this did, meant one unplayable source stopped the
           carousel permanently — the title just sat there. The grace period lets the cycle
           move on and try the next lesson instead. If readiness arrives first this effect
           re-runs, clears the timer below, and takes the normal hold. */
        const timer = setTimeout(
          () => setTypePhase("pausing"),
          demoVideoReadyForCycle ? 2500 : 4000,
        );
        return () => clearTimeout(timer);
      }
    }

    if (typePhase === "pausing") {
      // If unmuted while pausing, hold
      if (!demoMutedRef.current) {
        setTypePhase("holding");
        return;
      }
      setTypePhase("erasing");
      return;
    }

    if (typePhase === "erasing") {
      // If unmuted while erasing, snap to full title and hold
      if (!demoMutedRef.current) {
        setTypedText(title);
        setTypePhase("holding");
        return;
      }
      if (typedText.length > 0) {
        const timer = setTimeout(
          () => setTypedText(typedText.slice(0, -1)),
          30,
        );
        return () => clearTimeout(timer);
      } else {
        setActiveDemo((activeDemo + 1) % sampleVideos.length);
        setOverrideTitle(null);
        setTypePhase("typing");
      }
    }
  }, [
    demoActive,
    sampleVideos,
    activeDemo,
    typedText,
    typePhase,
    formData.text,
    overrideTitle,
    demoVideoReadyForCycle,
    hidden,
  ]);


  const handleInputChange = useCallback((field, value) => {
    if (field === "text" && value) trackFunnelEvent("type");
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError("");
  }, []);

  // Insert at the caret, not at the end — the whole point of a palette is reaching for a
  // glyph mid-sentence. Ported from the Workspace composer.
  const insertSymbol = useCallback((sym) => {
    const el = textareaRef.current;
    if (!el) {
      setFormData((prev) => ({ ...prev, text: prev.text + sym }));
      return;
    }
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? start;
    const next = el.value.slice(0, start) + sym + el.value.slice(end);
    setFormData((prev) => ({ ...prev, text: next }));
    setError("");
    // Restore focus and put the caret AFTER the glyph on the next frame, once React has
    // written the new value — doing it synchronously gets overwritten by the re-render.
    requestAnimationFrame(() => {
      el.focus();
      const at = start + sym.length;
      el.setSelectionRange(at, at);
    });
  }, []);

  // Close the palette on an outside click. The button is excluded so its own onClick can
  // toggle rather than being closed here and immediately reopened.
  useEffect(() => {
    if (!symbolsOpen) return;
    const onDown = (e) => {
      const inBar = symbolsRef.current?.contains(e.target);
      const inBtn = symbolsBtnRef.current?.contains(e.target);
      if (!inBar && !inBtn) setSymbolsOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setSymbolsOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [symbolsOpen]);

  // Auto-resize textarea
  const autoResizeTextarea = () => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = ta.scrollHeight + "px";
  };

  useEffect(() => {
    autoResizeTextarea();
  }, [formData.text]);

  // Cleanup blob URL on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    };
  }, [imagePreview]);

  // Reset form after queuing (moved from render body to useEffect)
  useEffect(() => {
    if (isQueued) {
      const timer = setTimeout(() => {
        setFormData((prev) => ({
          text: "",
          pdfFile: null,
          format: prev.format,
        }));
        if (imagePreview) {
          URL.revokeObjectURL(imagePreview);
          setImagePreview(null);
        }
        setError("");
        setIsQueued(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isQueued, imagePreview]);

  // Drag-and-drop handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    // Announces this as a copy target. Without it the cursor shows the "no drop" symbol over a
    // card that does in fact accept the file.
    if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
  }, []);
  // A COUNTER, not a boolean. dragenter/dragleave fire for every descendant the pointer
  // crosses, so dragging in over the textarea raised leave-then-enter and the highlight
  // strobed the whole way in. Counting means it only clears once the pointer has really left.
  const dragDepth = useRef(0);
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current += 1;
    setIsDragging(true);
  }, []);
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDragging(false);
  }, []);

  const isPdf = (file) =>
    file.type === "application/pdf" || /\.pdf$/i.test(file.name || "");

  const handleFileAttach = useCallback(
    (file) => {
      // Revoke old blob URL before creating a new one to prevent memory leaks
      if (imagePreview) URL.revokeObjectURL(imagePreview);

      // A PDF is a homework set or a lecture, not a prompt. It used to be flattened into
      // the text box as `//`-delimited questions, so the same file did one thing here and a
      // different thing on the Workspace — where "Homework PDF" opens the extract/select/
      // create flow. Same file, same flow now; the composer only ever sees typed text.
      if (isPdf(file)) {
        setImagePreview(null);
        handleInputChange("pdfFile", null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        // Off to the Workspace, WITH the file. Opening the chooser here put a New Project
        // modal over the marketing page — you attached a PDF from the hero and the thing you
        // were making had nowhere to appear. The same modal opens on the Workspace, where
        // the project it creates will live and where the rest of your work is visible behind
        // it. No type is passed: a PDF alone cannot say whether it is homework or a lecture,
        // so the modal still asks. The typed text becomes the title, as it does on Workspace.
        setPendingUpload([file]);
        const topic = formData.text.trim();
        navigate(`/workspace?mode=upload${topic ? `&text=${encodeURIComponent(topic)}` : ""}`);
        return;
      }

      handleInputChange("pdfFile", file);
      // An IMAGE stays on the old path on purpose: a photo of one problem is a prompt, and
      // extracting it into the box is how you then hit generate on it.
      const url = URL.createObjectURL(file);
      setImagePreview(url);
      handlePdfUpload(file);
    },
    [imagePreview, handleInputChange, formData.text, navigate],
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragDepth.current = 0;
      setIsDragging(false);
      // Dropping a file is metered on the server by a daily quota, not sold — see
      // handleSelectType in CreateProjectModal.
      //
      // A DROP GOES TO THE CHOOSER, INCLUDING AN IMAGE — which is where this diverges from the
      // attach BUTTON beside it. That button still treats an image as a prompt (handleFileAttach:
      // "a photo of one problem is a prompt"), and only sends PDFs to the project flow. Dragging
      // a file onto the box is a different gesture with a different expectation: you are handing
      // over a document, and the chooser accepts png/jpg/heic exactly as it accepts pdf — a
      // photographed worksheet is homework whether it arrived as a scan or a snapshot.
      // THE TWO PATHS NOW DISAGREE for images, deliberately and visibly, rather than silently.
      const files = Array.from(e.dataTransfer.files || []).filter((f) =>
        /\.(pdf|png|jpe?g|heic)$/i.test(f.name || ""),
      );
      if (!files.length) return;
      setPendingUpload(files);
      const topic = formData.text.trim();
      navigate(`/workspace?mode=upload${topic ? `&text=${encodeURIComponent(topic)}` : ""}`);
    },
    [formData.text, navigate],
  );

  const handleFileRemove = useCallback(() => {
    handleInputChange("pdfFile", null);
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
      setImagePreview(null);
    }
    setIsExtracting(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [imagePreview, handleInputChange]);

  // Images only now — PDFs go to the project flow (see handleFileAttach). The name stays
  // because the backend endpoint and the form field are both still called "pdf".
  const handlePdfUpload = async (file) => {
    if (!file) return;

    setIsExtracting(true);
    setError("");

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("pdf", file);
      if (user) formDataToSend.append("user_id", user.uid);

      const data = await apiService.extractPdfQuestions(formDataToSend);
      // Join extracted questions into textarea with // delimiter
      const newQuestions = data.questions.map((q) => q.text).join("\n//\n");
      // Append to existing text so users can upload multiple files sequentially
      const existing = formData.text?.trim();
      const combined = existing
        ? `${existing}\n//\n${newQuestions}`
        : newQuestions;
      handleInputChange("text", combined);
      handleInputChange("pdfFile", null);
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
        setImagePreview(null);
      }
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      console.error("PDF extraction error:", err);
      setErrorTone("error");
      setError(GENERIC_ERROR_MESSAGE);
    } finally {
      setIsExtracting(false);
    }
  };

  // Parse text segments split by //
  const getTextSegments = () => {
    if (!formData.text.trim()) return [];
    return formData.text
      .split("//")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  };

  const getActualCreditCost = () => {
    const segments = getTextSegments();
    return Math.max(segments.length, 1);
  };

  const smoothScrollTo = useCallback((ref, duration = 600, block = 'center', offsetPx = 0) => {
    const el = ref.current;
    if (!el) return;
    const start = window.scrollY;
    const startTime = performance.now();
    const ease = t => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    const step = (now) => {
      const rect = el.getBoundingClientRect();
      let target;
      if (block === 'center') {
        target = window.scrollY + rect.top + rect.height / 2 - window.innerHeight / 2;
      } else {
        target = window.scrollY + rect.top;
      }
      target = Math.max(0, target + offsetPx);
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      window.scrollTo(0, start + (target - start) * ease(progress));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, []);

  const handleInlineCardView = useCallback((job) => {
    if (job.state !== 'completed') return;
    trackFunnelEvent('watch');
    apiService.trackClick(job.id).catch(() => {});
    const vid = job.videoId || job.id;
    setInlineVideo({
      videoId: vid,
      title: job.title || 'Generated Video',
    });
    /* Pausing is declarative: `reelPaused` reads `inlineVideo`, so setting it above has
       already stopped the reel. Only the mute preference is ours to set. */
    setDemoMuted(true);
    demoMutedRef.current = true;
    /* `setForceShowPopular(false)` stood here. The state it set was removed; the call was not,
       so clicking a finished card threw a ReferenceError before the scroll below ever ran.
       Nothing reads `forceShowPopular` anywhere any more, so there is nothing to restore. */
    setTimeout(() => smoothScrollTo(inlinePlayerRef, 600, 'center', -35), 100);
  }, []);

  const handleCloseInlineVideo = useCallback(() => {
    // Clearing this clears `reelPaused`, and the reel resumes the clip on screen itself.
    setInlineVideo(null);
    inlineFollowUpSending.current = false;
  }, []);

  const handleInlineFollowUp = async (text) => {
    if (!text?.trim() || !user || inlineFollowUpSending.current || !generateUnified) return;
    inlineFollowUpSending.current = true;
    try {
      await generateUnified(text.trim(), user.uid, { parentVideoId: inlineVideo.videoId });
    } catch (err) {
      console.error("Follow-up generation failed:", err);
    } finally {
      inlineFollowUpSending.current = false;
    }
  };

  const hasTrackedFirstVideoIDRef = useRef(false);

  const activeTrackedJobRef = useRef(null);
  useEffect(() => {
    if (!generationQueue?.queue || !user) return;

    const queue = generationQueue.queue;

    // Get latest processing job
    const processingJob = [...queue]
      .reverse()
      .find((job) => job.state === "processing");

    const completedJob = [...queue]
      .reverse()
      .find((job) => job.state === "completed");

    // -------------------------
    // START TRACKING
    // -------------------------
    if (processingJob && activeTrackedJobRef.current !== processingJob.id) {
      console.log("⏱️ Start tracking wait time for job:", processingJob.id);

      activeTrackedJobRef.current = processingJob.id;
      waitStartTimeRef.current = Date.now();

      // Clear any existing interval
      if (waitIntervalRef.current) {
        clearInterval(waitIntervalRef.current);
      }

      waitIntervalRef.current = setInterval(() => {
        const elapsed = Math.floor(
          (Date.now() - waitStartTimeRef.current) / 1000,
        );

        // Write every 5 seconds (NOT every second)
        if (elapsed % 5 === 0) {
          apiService.trackAnalytics(user.uid, "waitTimeLastVideo", elapsed).catch(() => {});
        }
      }, 1000);
    }

    if (completedJob && activeTrackedJobRef.current === completedJob.id) {
      console.log("✅ Video completed, marking analytics");

      if (waitIntervalRef.current) {
        clearInterval(waitIntervalRef.current);
        waitIntervalRef.current = null;
      }

      apiService.trackAnalytics(user.uid, "waitTimeLastVideo", "completed").catch(() => {});

      activeTrackedJobRef.current = null;
    }

    return () => {};
  }, [generationQueue?.queue, user]);

  /* Touching the composer is the earliest honest signal that the Workspace is about to be
     needed. Fired once — App latches it — and deliberately NOT on page load: warming paints a
     whole grid and requests its thumbnails, which is work a visitor who never submits
     anything should not pay for. */
  const warmedRef = useRef(false);
  const warmWorkspace = useCallback(() => {
    if (warmedRef.current) return;
    warmedRef.current = true;
    window.dispatchEvent(new Event("magi:warm-workspace"));
  }, []);

  /* Submitting sends you to the Workspace, where the job is already showing as a card.

     IT IS A CUT, NOT A MORPH — deliberately, after trying the morph properly. Both composers
     still carry the shared `view-transition-name`, and the browser will happily tween the hero
     card into the docked bar; the problem is what that costs on this particular pair of pages.
     A ::view-transition-group animates position AND size, which is main-thread work, and it
     lands on the exact frames where the Workspace — always mounted, never painted — reveals a
     whole grid of cards and thumbnails at once. Four separate reductions (holding the page's
     fetches until the animation ended, dropping the full-page root snapshot, shortening the
     duration, pre-warming the grid) each made it better and none made it good.
     A cut has nothing to drop frames during. The composer being in a different place on the
     new page is legible on its own, and an animation that stutters communicates less than no
     animation at all.
     Everything else is left in place — `runViewTransition`, the shared name, the fetch gate —
     so restoring the morph is putting this one call back. */
  /* Hand off the composer's geometry and go. NOTHING ANIMATES HERE — an earlier attempt had
     the card shrink on this page before navigating, which just made Home look like it was
     rearranging itself for no reason. The whole hand-off plays on the Workspace, out of this
     page's geometry; see utils/composerHandoff. A missing ref degrades to a plain cut. */
  const composerCardRef = useRef(null);

  /* THE NAV LANDING 70px DOWN THE PAGE ON THIS CUT, and what it actually took to fix.
   *
   * You reach the submit control by scrolling, so Safari's URL bar is COLLAPSED when you
   * press it. The cut resets scroll to the top, the URL bar expands again, and the visual
   * viewport shifts by about its height. iOS does not re-anchor fixed elements for that — it
   * re-anchors them on a real scroll — so the nav stays painted for the viewport of a frame
   * ago and sits on top of the first row of cards.
   *
   * THE KEYBOARD WAS NOT IT. Blurring first was the previous attempt at this and it shipped
   * and did not fix it, so the blur stays only because a keyboard has no business surviving a
   * page change, not because it addresses the bug. The evidence that settled it: the bar holds
   * its wrong position while the cards scroll underneath, and scrolling far enough to collapse
   * the URL bar again snaps it home by itself. Still fixed, wrong origin, self-correcting —
   * which rules out both a lost `fixed` and a bad containing block.
   */
  /* Blur, and RESOLVE ONLY ONCE THE KEYBOARD HAS ACTUALLY GONE.
   *
   * While an input is focused iOS owns the scroll position — it holds the field in view and
   * undoes anything we write. The trace proved the keyboard was still up for the whole
   * transition: visualViewport.height read 348 at the moment of arrival, against 676 once it
   * had gone. So every reset, before OR after navigating, was being issued into a window
   * Safari was still steering.
   *
   * blur() is not synchronous about it — the keyboard animates out over a few hundred ms and
   * the viewport only comes back at the end of that. Waiting for the height to recover is the
   * only reliable signal that Safari has handed the scroll back.
   *
   * Bounded, and free when there is no keyboard: with nothing focused the height never changed
   * and this resolves on the first check. */
  const dismissKeyboard = useCallback(() => {
    const active = document.activeElement;
    if (active && typeof active.blur === "function") active.blur();

    const vv = window.visualViewport;
    if (!vv) return Promise.resolve();

    /* ASK THE VIEWPORT WHETHER THE KEYBOARD IS UP. DO NOT ASK WHAT IS FOCUSED.
     *
     * The previous version gated on activeElement.tagName being TEXTAREA or INPUT and bailed
     * out otherwise — which meant it never once waited. Tapping the send button moves focus
     * off the textarea, so by the time this runs activeElement is a BUTTON, the guard fails,
     * and the navigation proceeds with the keyboard still open. The trace shows it plainly:
     * vh=348 against ih=676 at the moment of arrival, a 328px shortfall that is the keyboard.
     *
     * The shortfall IS the signal, and it is unambiguous — measured 328 with the keyboard up
     * and 0 without, so a 100px threshold cannot misread either state. That also makes this
     * correct for the on-screen keyboard of any input, focused by any route.
     *
     * Why it matters: iOS offsets the visual viewport to hold the field above the keyboard
     * (offsetTop 67 here) and re-applies that offset on every subsequent layout change. Every
     * correction shipped so far has been fighting a state the page should never have been
     * carrying into the navigation at all. */
    const keyboardUp = () => window.innerHeight - vv.height > 100;
    if (!keyboardUp()) return Promise.resolve();

    return new Promise((resolve) => {
      // Long enough for a slow dismissal; the deadline only exists so a viewport that never
      // recovers cannot wedge the submit entirely.
      const deadline = performance.now() + 1200;
      const check = () => {
        if (!keyboardUp() || performance.now() > deadline) return resolve();
        requestAnimationFrame(check);
      };
      requestAnimationFrame(check);
    });
  }, []);

  const goToWorkspace = useCallback(() => {
    const card = composerCardRef.current;
    // Geometry first: the hand-off reads this rect, and the scroll below would move it.
    if (card) leaveComposerAt(card.getBoundingClientRect());

    /* RESET THE SCROLL BEFORE THE SWAP, NOT AFTER IT.
     *
     * Home, Workspace and Explore are all mounted at once and hidden with height:0 rather than
     * unmounted (see App.jsx), so they share ONE window scroll and navigating between them
     * resets nothing by itself. Whatever offset Home was at is simply still there when the
     * Workspace appears — it is not restored, it was never left.
     *
     * Resetting afterwards is what has failed seven times: by then the page is already at 0 as
     * far as the engine is concerned, so writing 0 over 0 is a no-op it can decline to repaint,
     * and on iOS it declines. Doing it HERE, while Home is still the visible page, makes it a
     * real 65->0 scroll with a real delta that has to be rendered — and the swap then happens
     * from a page that is genuinely at the top.
     *
     * The reset afterwards stays as the backstop for the viewport shifts that come later
     * (Safari's toolbars, the keyboard leaving); this is about the offset carried IN. */
    /* THREE STEPS, IN THIS ORDER, and each one finishes before the next starts:
     *   1. put the keyboard away and wait for the viewport to actually come back
     *   2. put HOME back at the top, as a real movement, and let it commit
     *   3. only then swap to the Workspace
     *
     * Step 2 is the one that has been missing. Every reset so far used scrollTo(0, 0) — an
     * ABSOLUTE write, and when the engine already reports 0 while rendering 67 it is free to
     * treat that as a no-op, which is exactly what it does. scrollBy is relative: it is a
     * movement, it clamps at the top, and there is no value it can compare against to decide
     * nothing needs doing. The magnitude is deliberately larger than any page can be. */
    const resetHomeToTop = () => new Promise((resolve) => {
      window.scrollBy(0, -1e6);
      // Two frames: one for the move to be committed, one for it to be painted, so the page
      // that swaps in is replacing a Home that is genuinely and visibly at the top.
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });

    dismissKeyboard().then(resetHomeToTop).then(() => {
      // This arrival is new work, not a return — land at the top of the Workspace rather than
      // at whatever offset it was last read at. See utils/arrival.
      arriveAtTop();
      navigate("/workspace");
      // After the URL bar has finished moving, not on the next frame — re-anchoring against a
      // viewport that is still animating fixes nothing. Inside the .then so it runs after the
      // swap rather than racing the keyboard wait it used to sit beside.
      /* Just the nav. forceRepaintPage — a display toggle on #main-content — used to run here
         too, and it is a full relayout and repaint of the entire page landing a few hundred ms
         into the arrival. It existed to paper over the inherited viewport offset; that offset
         is gone now that the keyboard is dismissed and Home is reset before the swap, so all
         it contributed was the hitch. The nav re-anchor stays: it is one small fixed element
         and it fixes a bug that is still real. */
      afterViewportSettles(reanchorFixedChrome);
    });
  }, [navigate, dismissKeyboard]);

  const handleGenerate = async () => {
    trackFunnelEvent("submit");
    const segments = getTextSegments();
    if (segments.length === 0) {
      setErrorTone("error");
      setError(t('home_enter_prompt'));
      return;
    }

    /* LOCAL "EMPTY" MODE — no credit check, no request, no job. Everything below this point
       talks to the backend, so the branch has to come first: the point of the mode is to
       exercise the generating tile and the hand-off to Workspace without spending anything.
       The entry only exists in a dev build (see composerConfig), so this is unreachable in
       production even if a stale client somehow named the mode. */
    if (formData.mode === DEV_EMPTY_MODE) {
      setFormData((prev) => ({ ...prev, text: "", pdfFile: null }));
      generationQueue?.addDevEmptyJob(segments[0]);
      goToWorkspace();
      return;
    }

    // Anonymous visitors are NOT credit-checked here. They have no users doc, so this check
    // read limit=0 and blocked every anonymous generation before the request was even sent —
    // which is why anonymous generation only ever worked in theory. Their allowance is the
    // server-side daily counter in ip_rate_limiter; a 429 comes back with the sign-in prompt.
    if (user && !user.isAnonymous) {
      const actualCost = getActualCreditCost();
      const limit = userData?.video_limit_this_month ?? 0;
      const used = userData?.video_spend_this_month ?? 0;
      const remaining = limit - used;

      if (remaining < actualCost) {
        if (userData?.analytics?.hitAuthFreeLimit == false) {
          apiService.trackAnalytics(user.uid, "hitAuthFreeLimit", true).catch(() => {});
        }
        setShowSubscriptionModal(true);
        return;
      }
    }

    // Clear input immediately so user sees instant feedback. Keep the user's
    // sticky prefs (format + model mode) — only the text/PDF inputs are cleared.
    const originalText = formData.text;
    setFormData((prev) => ({ ...prev, text: "", pdfFile: null }));
    goToWorkspace();

    try {
      setError("");
      if (segments.length > 1) {
        // Batch generation for multiple segments. Reels use the backend reel model;
        // lessons/solutions use the selected model mode.
        const batchOptions = { model: formData.format === "reel" ? "flash" : formData.mode };
        if (formData.format === "reel") batchOptions.contentType = "reel";
        if (detectedLanguage && detectedLanguage !== "en") batchOptions.language = detectedLanguage;
        console.log(
          "BATCH SUBMIT — format:",
          formData.format,
          "batchOptions:",
          JSON.stringify(batchOptions),
        );
        await generateUnifiedBatch(segments, user?.uid || "", batchOptions);

        if (user?.uid) {
          apiService.trackAnalytics(user.uid, "videos_generated", undefined, segments.length).catch(() => {});
        }
      } else {
        // Single generation — detect /reel prefix
        let text = segments[0];
        const genOptions = { model: formData.format === "reel" ? "flash" : formData.mode };
        if (detectedLanguage && detectedLanguage !== "en") {
          genOptions.language = detectedLanguage;
        }

        if (
          formData.format === "reel" ||
          text.trim().toLowerCase().startsWith("/reel")
        ) {
          genOptions.contentType = "reel";
          if (text.trim().toLowerCase().startsWith("/reel")) {
            text = text.trim().substring(5).trim();
          }
        }

        if (user?.uid) {
          apiService.trackAnalytics(user.uid, "videos_generated", undefined, 1).catch(() => {});
        }

        console.log("reached step 1");

        await generateUnified(text, user?.uid || "", genOptions);
      }
      setIsQueued(true);
      // No toast: the Workspace already shows the job as a card with its progress dial,
      // so a floating "it is generating" note would just be noise on top of it.

      // Refresh usage count after successful generation
      if (user && !isSubscriber && refreshDailyUsage) {
        refreshDailyUsage();
      }
    } catch (err) {
      if (err.status === 503) {
        // Busy, gated, or down — busyMessageFor picks the wording from the error CODE
        // so the backend's own text never reaches a user, and busyToneFor decides whether
        // this is a failure at all. "Our servers are full, come back later" is information.
        setErrorTone(busyToneFor(err));
        setError(busyMessageFor(err));
      } else if (err.status === 402 || err.status === 429 || err.status === 403) {
        // Out of allowance. An anonymous visitor has used their free one, so the answer is an
        // account — not the upgrade pitch, which they cannot even act on without signing in.
        if (refreshDailyUsage) refreshDailyUsage();
        if (user?.isAnonymous) {
          openLoginGate({
            title: t('auth_generate_title'),
            subtitle: t('auth_generate_desc'),
          });
        } else {
          setShowSubscriptionModal(true);
        }
      } else {
        setErrorTone("error");
        setError(GENERIC_ERROR_MESSAGE);
      }
      // Restore text so user doesn't lose their input on error, and go back to the page
      // that can show it — the error and the input both live on Home.
      setFormData((prev) => ({ ...prev, text: originalText }));
      setIsQueued(false);
      navigate("/");
    }
  };

  const segments = getTextSegments();
  const isMulti = segments.length > 1;
  const canSubmit = !isExtracting && formData.text.trim();

  const activeDemoVideo = sampleVideos[activeDemo];
  const activeDemoId = activeDemoVideo?.job_id || activeDemoVideo?.video_id;

  /* ── THE REEL ────────────────────────────────────────────────────────────────────────
   *
   *  Two <video> elements handing off to each other, so the chip never has a moment
   *  without a picture. The whole argument is in `useDemoReel`; what matters here is that
   *  this page no longer sequences load/play at all. It says WHICH lesson should be on
   *  screen and which one is next, and the reel does the rest.
   *
   *  `nextId` is the lookahead. A cycle is ~5s and a clip takes ~400ms to load, so the
   *  next one is warm long before it is wanted and the hand-off is instant. When the user
   *  clicks a card instead (`playingDemoId`), the reel has not warmed that one — it keeps
   *  the current clip playing and swaps when the new one has a frame, which is the branch
   *  that used to be a black box. */
  const currentPreviewId = playingDemoId || activeDemoId;
  const nextDemo = sampleVideos[(activeDemo + 1) % (sampleVideos.length || 1)];
  const nextPreviewId = playingDemoId ? null : (nextDemo?.job_id || nextDemo?.video_id);

  const reelPaused = Boolean(
    hidden || expandedForm || inlineVideo || (isGeneratingMode && !playingDemoId),
  );
  const onReelReady = useCallback(() => setDemoVideoReadyForCycle(true), []);

  const { aRef: reelARef, bRef: reelBRef, frontIsA } = useDemoReel({
    playlist: sampleVideos,
    currentId: currentPreviewId,
    nextId: nextPreviewId,
    srcFor: demoVideoSrc,
    muted: demoMuted,
    paused: reelPaused,
    /* `hidden` is the page being off screen entirely, which is a stronger statement than
       `paused` — HomePage is never unmounted, only hidden, so without this the reel would
       sit on two open video streams for a page nobody is looking at. */
    idle: hidden,
    onReady: onReelReady,
  });

  /* The cycle may not advance until the clip on screen has a frame (see the typewriter), so
     readiness has to be WITHDRAWN when the chosen lesson changes and re-granted by the reel.
     Without this the incoming title would spend its 2.5s hold against the previous clip's
     readiness and every hand-off would look early. */
  useEffect(() => { setDemoVideoReadyForCycle(false); }, [currentPreviewId]);

  // Sync demoMuted to ref and module-level cache
  useEffect(() => {
    demoMutedRef.current = demoMuted;
    cachedDemoMuted = demoMuted;
  }, [demoMuted]);

  // Truncate long titles (API sometimes returns full prompts as titles)
  const truncateTitle = (video) => {
    const raw = video?.title || video?.topic || "";
    return raw.length > 60 ? raw.slice(0, 57) + "..." : raw;
  };

  const popularHiding = Boolean(inlineVideo);

  return (
    <>
      <div className="fixed inset-0 bg-[#F8FAFC] -z-10" />

      {/* EVERYTHING BELOW RUNS AT 0.8 FROM lg UP — the page as it looks at browser zoom 80%,
          which is where it was asked to live. The rule and the whole reasoning are on
          `.home-scale` in index.css; it is `zoom`, not a transform, so the layout really is
          25% roomier rather than just painted smaller.
          The flat ground above, the toast (it portals to <body>) and the two modals at the
          foot of this fragment are outside it on purpose: an overlay centred on the window
          should not be the one thing on screen at 80%. */}
      <div className="relative home-scale">
        <div
          // min-h-screen from md ONLY. On a phone it reserved a full 852px viewport for ~609px
          // of content, so a 240px band of white sat between the popular row and the cards
          // below it. On desktop the row is wide enough to earn the full screen; on a phone it
          // is not, and the page should simply be as tall as what is in it.
          // The top padding is a CLASS so `short:` can override it; an inline padding would
          // beat every stylesheet rule and leave no way in. The +1.5rem replaces the top bar
          // that used to sit above this page — without it the hero rides against the top of
          // the window now that navigation is a left rail. The transition stays inline and
          // still animates a class-set padding.
          //
          // `short:pt-3` is the landscape phone. The fixed 1.5rem is what dominates here (4vh
          // of a 393px viewport is only 16px), so the pair came to 40px above a greeting that
          // already sits under a 64px navigation bar — a quarter of the fold spent before the
          // first word. 12px is enough to not touch the bar.
          className={`md:min-h-screen flex flex-col items-center px-4 sm:px-6 pb-20 md:pb-8 short:pt-3 ${
            expandedForm ? 'pt-[calc(7vh+1.5rem)]' : 'pt-[calc(4vh+1.5rem)]'
          }`}
          style={{ transition: 'padding-top 600ms cubic-bezier(0.4, 0, 0.2, 1)' }}
        >
          <div className="w-full max-w-2xl lg:max-w-6xl">
            {/* Greeting */}
            <h1
              // The margins are CLASSES for the same reason as the padding above — `short:`
              // cannot get under an inline style. They still do what they did: the heading
              // sits LOWER by taking margin from below and giving it back above, rather than
              // by padding the page, because everything under it is in normal flow and
              // pushing the page down would carry the composer and player with it, so the
              // heading would not appear to move at all.
              //
              // The three `short:` values are the landscape phone, where the fold is 393px.
              // `sm:text-4xl` fires there (852px wide clears the 640px breakpoint), so the
              // greeting was rendering at 36px with 24px of margin on each side — about 85px
              // for one line of text. Back to the portrait 24px and half the margins.
              className={`text-2xl sm:text-4xl md:text-5xl lg:text-6xl short:text-2xl font-bold text-gray-900 text-center tracking-tight leading-tight mt-6 short:mt-2 short:mb-3 ${
                expandedForm ? 'mb-8' : 'mb-6'
              }`}
              style={{ transition: 'margin-bottom 600ms cubic-bezier(0.4, 0, 0.2, 1)' }}
            >
              {t('home_hero')}<span className="text-[#635BFF]">{t('home_hero_accent')}</span>
              <img src="/magi-icon.svg" alt="" className="inline-block w-8 h-8 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 ml-1.5 sm:ml-2 -mt-1 rounded-xl" />
            </h1>

            <div
              className="lg:grid lg:items-center"
              style={{
                gridTemplateColumns:
                  sampleVideos.length > 0
                    ? (expandedForm || (isGeneratingMode && !playingDemoId))
                      ? "1fr 0fr"
                      : "7fr 5fr"
                    : "1fr",
                gap: (expandedForm || (isGeneratingMode && !playingDemoId)) ? 0 : "2rem",
                transition:
                  "grid-template-columns 600ms cubic-bezier(0.4, 0, 0.2, 1), gap 600ms cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              {/* z-30 is load-bearing, not decoration. The composer card sets
                  `view-transition-name: magi-composer` for the Home -> Workspace morph, and
                  view-transition-name CREATES A STACKING CONTEXT — so the mode menu's z-50
                  is scoped inside the card and cannot escape it. The card itself was z-auto,
                  which left it losing to anything later in the document: POPULAR LESSONS
                  painted straight over the open menu and sliced it off mid-"Lecture". The
                  menu was never overflowing the viewport (248px of room below it), so no
                  amount of z-index ON THE MENU could have fixed this — the z-index has to go
                  on an ancestor OUTSIDE the card's stacking context. This is that ancestor. */}
              <div className="relative z-30 min-w-0">
                {/* Input Container — solid white card */}
                <div
                  data-onboarding="textarea"
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  ref={composerCardRef}
                  /* The drag state changes COLOUR ONLY — border and ground — never geometry.
                     A ring adds width, and a target that grows under the pointer is harder to
                     hit than one that holds still. The old state was a 40%-opacity ring, which
                     at that strength reads as a focus glow rather than "let go here". */
                  className={`relative rounded-2xl transition-colors duration-150 shadow-[0_1px_3px_rgba(0,0,0,0.04),0_6px_24px_rgba(0,0,0,0.06)] ${
                    isDragging
                      ? "bg-[#EAF3FF] border-2 border-[#0076E5]"
                      : "bg-white border border-gray-200/70"
                  }`}
                >
                  {/* Over the card, not in its layout, so nothing shifts while you are aiming.
                      pointer-events-none matters: an element under the cursor that is not the
                      drop target swallows dragleave and strands the highlight on. */}
                  {isDragging && (
                    <div className="absolute inset-0 z-20 rounded-2xl flex items-center justify-center
                      pointer-events-none bg-[#EAF3FF]">
                      <span className="text-[#0076E5] text-[15px] font-bold">
                        {t('composer_drop_hint')}
                      </span>
                    </div>
                  )}
                  {/* File processing state — full-height takeover */}
                  {formData.pdfFile && isExtracting ? (
                    <div
                      className="flex items-center gap-4 px-5 py-5"
                      style={{ minHeight: "80px" }}
                    >
                      {imagePreview ? (
                        <div className="relative flex-shrink-0">
                          <img
                            src={imagePreview}
                            alt="Preview"
                            className="h-16 w-16 object-cover rounded-lg border border-gray-200"
                          />
                        </div>
                      ) : (
                        <div className="relative flex-shrink-0 flex flex-col items-center justify-center w-16 h-16 bg-gray-100 rounded-lg">
                          <FileText className="w-6 h-6 text-gray-400" />
                          <span className="text-[10px] text-gray-500 font-medium mt-0.5">
                            {formData.pdfFile.name
                              .split(".")
                              .pop()
                              ?.toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-700 truncate">
                          {formData.pdfFile.name}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <Loader className="w-3.5 h-3.5 text-blue-500 animate-spin" />
                          <span className="text-xs text-gray-400">
                            {t('home_extracting')}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={handleFileRemove}
                        className="flex-shrink-0 p-1 text-gray-300 hover:text-red-400 transition-colors"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  ) : (
                    <div className="relative">
                      <textarea
                        ref={textareaRef}
                        value={formData.text}
                        onChange={(e) => {
                          const value = e.target.value;

                          setDemoActive(false);
                          setTypedText("");
                          handleInputChange("text", value);

                          // Only expand when user actually types something
                          if (value.trim().length > 0) {
                            // The reel pauses itself: `reelPaused` reads `expandedForm`.
                            setExpandedForm(true);
                          }
                        }}
                        onFocus={() => {
                          // Stop the typewriter placeholder so it is not animating text into
                          // a field the cursor is now sitting in.
                          setDemoActive(false);
                          setTypedText("");
                          // Start painting the Workspace now, while nothing is animating —
                          // submitting from here is what it costs otherwise. See App.jsx.
                          warmWorkspace();
                          // Deliberately does NOT pause the preview video. Focusing is not
                          // intent to stop watching — clicking the box, or tabbing through
                          // it, would kill playback before a single character was typed.
                          // onChange pauses once there is actually text (via expandedForm).
                        }}
                        onBlur={() => {
                          if (
                            !formData.text.trim() &&
                            sampleVideos.length > 0
                          ) {
                            setDemoActive(true);
                            setTypePhase("typing");
                            setTypedText("");
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            if (canSubmit) {
                              handleGenerate();

                              if (
                                userData?.analytics?.submittedFirstPrompt ==
                                false
                              ) {
                                apiService.trackAnalytics(user.uid, "submittedFirstPrompt", true).catch(() => {});
                              }
                            }
                          }
                        }}
                        placeholder={
                          demoActive && sampleVideos.length > 0
                            ? ""
                            : t('home_placeholder')
                        }
                        className="w-full px-5 pt-5 pb-2 bg-transparent text-gray-900 placeholder-gray-400 text-base focus:outline-none resize-none"
                        style={{
                          minHeight: "80px",
                          maxHeight: "300px",
                          overflowY: "auto",
                        }}
                      />
                      {demoActive &&
                        !formData.text &&
                        sampleVideos.length > 0 && (
                          <div className="absolute top-0 left-0 right-0 px-5 pt-5 pb-2 text-gray-400 text-base pointer-events-none">
                            {typedText}
                            <span className="inline-block w-0.5 h-5 bg-blue-400 ml-0.5 animate-pulse align-middle" />
                          </div>
                        )}
                    </div>
                  )}

                  {/* Bottom toolbar */}
                  <div className="flex items-center justify-between px-3 pb-3">
                    <div className="flex items-center gap-1.5">
                      <button
                        data-onboarding="upload"
                        // No subscriber check: reading a document is metered by a daily
                        // quota on the server (settings.PDF_EXTRACTS_PER_DAY), not sold.
                        onClick={() => fileInputRef.current?.click()}
                        className="p-2.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                        title="Upload PDF or image"
                      >
                        <Upload className="w-5 h-5" />
                      </button>

                      {/* Math symbols. The palette itself is NOT here — it floats above the
                          whole card (rendered before it), so it never has to fit inside the
                          toolbar or push the controls around. */}
                      <button
                        ref={symbolsBtnRef}
                        type="button"
                        onClick={() => setSymbolsOpen((o) => !o)}
                        aria-label="Insert a math symbol"
                        aria-expanded={symbolsOpen}
                        className={`flex items-center justify-center w-9 h-9 rounded-full border text-[17px] transition-colors ${
                          symbolsOpen
                            ? "bg-gray-900 text-white border-gray-900"
                            : "bg-white text-gray-700 border-gray-300/80 hover:bg-gray-50"
                        }`}
                      >
                        <span
                          className="leading-none"
                          style={{ fontFamily: "'Times New Roman', serif" }}
                        >
                          &#8730;x
                        </span>
                      </button>
                    </div>

                    <div className="flex items-center gap-2">
                      {/* Model picker — the toolbar's only selector */}
                      <ModeDropdown
                        value={formData.mode}
                        onChange={(val) => {
                          // Homework / Lecture / Project are not models — they are the
                          // upload-and-extract flows that live on the Workspace. Selecting
                          // one here hands off rather than setting a mode the generate call
                          // would then send to the backend as a model id and have rejected.
                          const picked = MODE_OPTIONS.find((m) => m.value === val);
                          if (picked?.input === "handoff") {
                            const typed = formData.text.trim();
                            navigate(
                              `/workspace?mode=${val}` +
                                (typed ? `&text=${encodeURIComponent(typed)}` : ""),
                            );
                            return;
                          }
                          handleInputChange("mode", val);
                        }}
                        isSubscriber={isSubscriber}
                        onLocked={() => setShowSubscriptionModal(true)}
                        t={t}
                      />

                      {/* Submit button */}
                      <button
                        data-onboarding="submit"
                        onClick={() => {
                          handleGenerate();

                          if (
                            userData?.analytics?.submittedFirstPrompt == false
                          ) {
                            apiService.trackAnalytics(user.uid, "submittedFirstPrompt", true).catch(() => {});
                          }
                        }}
                        disabled={!canSubmit}
                        className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                          canSubmit
                            ? "bg-[#635BFF] hover:bg-[#5851e0] text-white"
                            : "bg-gray-200 text-gray-400 cursor-not-allowed"
                        }`}
                      >
                        <ArrowUp className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.heic,.heif,image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        handleFileAttach(file);
                      }
                    }}
                    className="hidden"
                  />
                </div>
                {/* Symbol bar sits BELOW the composer, in normal flow — it pushes the
                    examples and POPULAR LESSONS down rather than floating over them. As an
                    overlay above the card it covered the "Learn anything, visually" headline,
                    which is the one thing on the page you do not want to hide. (The Workspace
                    copy still floats ABOVE its bar, because that composer is docked to the
                    bottom of the window and has nothing below it to push.) */}
                {/* Animated by GRID ROWS (0fr -> 1fr), not by mounting and unmounting.
                    Conditional rendering made the bar appear in one frame, and because it
                    lives in normal flow everything under it jumped at once — that snap is
                    what read as jitter, not the speed of any fade. Animating the track
                    height means the space itself opens over 220ms and the content below
                    slides rather than teleports. 0fr -> 1fr is used instead of a max-height
                    guess because the bar's height is content-derived; a max-height large
                    enough to be safe would spend most of the duration animating nothing. */}
                <div
                  className="grid transition-all duration-[220ms] ease-out"
                  style={{
                    gridTemplateRows: symbolsOpen ? "1fr" : "0fr",
                    opacity: symbolsOpen ? 1 : 0,
                  }}
                  aria-hidden={!symbolsOpen}
                >
                  {/* The clipper. Margin lives INSIDE it so the gap collapses with the bar —
                      on the outer grid it would leave a permanent 8px hole when closed. */}
                  <div className="overflow-hidden min-h-0">
                  <div
                    ref={symbolsRef}
                    className={`mt-2 rounded-2xl bg-white border border-gray-200 px-2 py-2 overflow-x-auto ${
                      symbolsOpen ? "" : "pointer-events-none"
                    }`}
                    style={{
                      boxShadow: "0 8px 26px rgba(15,23,42,0.13), 0 2px 6px rgba(15,23,42,0.07)",
                      scrollbarWidth: "none",
                    }}
                  >
                    <div className="flex items-center gap-3 w-max">
                      {SYMBOL_GROUPS.map((g, gi) => (
                        <React.Fragment key={g.label}>
                          {gi > 0 && <span className="w-px h-7 bg-gray-200 flex-shrink-0" />}
                          <div className="flex items-center gap-0.5">
                            {g.items.map((sym) => (
                              <button
                                key={sym}
                                type="button"
                                onClick={() => insertSymbol(sym)}
                                title={g.label}
                                /* Kept out of the tab order while collapsed — the bar stays
                                   mounted so it can animate, and 60 invisible buttons in the
                                   tab order is worse than the jitter this replaced. */
                                tabIndex={symbolsOpen ? 0 : -1}
                                className="w-9 h-9 rounded-lg text-[16px] text-gray-800 hover:bg-gray-100 active:bg-gray-200 transition-colors flex-shrink-0"
                                style={{ fontFamily: "'Times New Roman', serif" }}
                              >
                                {sym}
                              </button>
                            ))}
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                  </div>
                </div>


                <div className="flex justify-between items-center mt-2">
                  <div>
                    {isMulti && (
                      <p className="text-gray-500 text-sm">
                        {t('home_videos_generated', { n: segments.length })}
                      </p>
                    )}
                  </div>

                  {user && userData && (
                    <p className="text-gray-400 text-xs">
                      {formData.text.trim() && (
                        <span className="text-gray-500 mr-1.5">
                          {t('home_credits', { n: getActualCreditCost() })} ·
                        </span>
                      )}
                      {(userData.video_limit_this_month || 0) -
                        (userData.video_spend_this_month || 0)}{" "}
                      {t('home_remaining')}
                    </p>
                  )}
                </div>

                {/* Example topic chips — fade out when typing, scroll horizontally on mobile */}
                <div
                  className={`flex gap-2 mt-3 transition-all duration-500 ease-in-out ${
                    formData.text.trim()
                      ? "max-h-0 opacity-0 mt-0 overflow-hidden"
                      : "max-h-20 opacity-100"
                  } overflow-x-auto md:flex-wrap md:overflow-x-visible`}
                  style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch", msOverflowStyle: "none" }}
                >
                  {EXAMPLE_TOPICS.map((topic) => (
                    <button
                      key={topic}
                      onClick={() => {
                        setDemoActive(false);
                        setTypedText("");
                        handleInputChange("text", topic);
                        setExpandedForm(true);   // the reel pauses itself — see reelPaused
                      }}
                      className="flex-shrink-0 px-3.5 py-1.5 rounded-full text-sm bg-white text-gray-600 border border-gray-200/80 shadow-[0_1px_2px_rgba(0,0,0,0.04)]
                             hover:bg-gray-50 hover:text-gray-900 hover:border-gray-300 transition-all whitespace-nowrap"
                    >
                      {topic}
                    </button>
                  ))}
                </div>

                <OnboardingBubbles />

                {/* Error — absolute overlay so it doesn't push content down */}
                {error && (
                  <div
                    className="absolute left-0 right-0 flex justify-center pointer-events-none"
                    style={{ top: '100%', marginTop: '8px', zIndex: 30 }}
                  >
                    {/* BLUE WHEN NOTHING IS WRONG. "Our servers are full, come back later" is
                        a status, not a failure — red says the request broke and invites a
                        retry that will not help. Same position, different reading.

                        A SPEECH BUBBLE, pointing at the box it is about. A floating rectangle
                        below the input reads as a separate element on the page; the tail says
                        "this is about THAT" without a word of copy spent on it. */}
                    <div className={`relative px-4 py-2.5 border rounded-xl shadow-sm pointer-events-auto ${
                      errorTone === 'info'
                        ? 'bg-blue-50 border-blue-200'
                        : 'bg-red-50 border-red-200'}`}>
                      {/* The tail: a square rotated 45°, showing only the two borders that
                          form its point. Its fill is the bubble's own, so it covers the top
                          border where they meet and the outline reads as one shape. */}
                      <span
                        aria-hidden="true"
                        className={`absolute left-1/2 -top-[6px] w-[11px] h-[11px] -translate-x-1/2
                                    rotate-45 border-l border-t ${
                          errorTone === 'info'
                            ? 'bg-blue-50 border-blue-200'
                            : 'bg-red-50 border-red-200'}`}
                      />
                      <p className={`relative text-sm font-medium ${
                        errorTone === 'info' ? 'text-blue-700' : 'text-red-600'}`}>{error}</p>
                    </div>
                  </div>
                )}

                {/* Queued confirmation — a toast pinned under the header, NOT a banner in the
                    page flow. Anchored below the input it landed between the prompt chips and
                    the results grid, reading as a third thing in the column rather than as
                    feedback. Portalled to <body> because `fixed` resolves against the nearest
                    transformed ancestor, and this sits deep inside the hero layout. */}
                {successMsg && createPortal(
                  <div className="fixed top-[72px] right-5 z-[60] pointer-events-none">
                    <div className="toast-enter flex items-start gap-2.5 max-w-[290px] px-3.5 py-2.5 bg-blue-50 border border-blue-200 rounded-xl shadow-sm pointer-events-auto">
                      <Check className="w-4 h-4 text-blue-600 mt-[2px] shrink-0" />
                      <p className="text-blue-700 text-[13px] font-medium leading-snug">{successMsg}</p>
                    </div>
                  </div>,
                  document.body,
                )}

              </div>

              {DEMO_VIDEO_IDS.length > 0 && (
                /* The rounding and shadow live on THIS element — the one that does the
                   clipping — not on the card inside it. overflow-hidden is required so the
                   card clips as this grid column animates to 0fr, but the card filled the
                   column exactly, so that same clip sliced its right corners square and cut
                   the shadow off. An element's own box-shadow is not clipped by its own
                   overflow, so painting it here lets it fall outside the clip box. */
                <div
                  className={`hidden lg:block overflow-hidden min-w-0 rounded-2xl shadow-[0_2px_8px_rgba(0,0,0,0.04),0_8px_32px_rgba(0,0,0,0.08)] ${
                    (expandedForm || (isGeneratingMode && !playingDemoId)) ? "pointer-events-none" : ""
                  }`}
                  style={{
                    opacity: (expandedForm || (isGeneratingMode && !playingDemoId)) ? 0 : 1,
                    transition: 'opacity 600ms cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <div
                    ref={demoPlayerRef}
                    className="rounded-2xl overflow-hidden bg-white cursor-pointer group"
                    onClick={() => {
                      /* STATE ONLY. The reel applies `muted` to both elements itself, so
                         reaching into the DOM here would be a second author for one fact —
                         and with two elements it would reach the wrong one half the time. */
                      {
                        const wasMuted = demoMutedRef.current;
                        setDemoMuted(!wasMuted);
                        demoMutedRef.current = !wasMuted; // sync now: the typewriter reads it
                        if (wasMuted) {
                          // Unmuting — continue typing from current position, then hold
                          const currentVideo = playingDemoId
                            ? popularLessonVideos.find(
                                (v) =>
                                  (v.job_id || v.video_id) === playingDemoId,
                              )
                            : sampleVideos[activeDemo];
                          const title = (
                            currentVideo?.topic ||
                            currentVideo?.title ||
                            ""
                          ).slice(0, 60);
                          if (title) {
                            setOverrideTitle(title);
                            setTypePhase("typing");
                          }
                        } else {
                          // Muting — resume the typewriter cycle
                          setOverrideTitle(null);
                          setTypePhase("pausing");
                        }
                      }
                    }}
                  >
                    <div className="relative aspect-video bg-black rounded-t-2xl overflow-hidden">
                      {/* TWO ELEMENTS, ONE VISIBLE. The hidden one is already holding the
                          next lesson's first frame, so a hand-off is a crossfade between
                          two real pictures rather than a gap to be covered. See
                          `useDemoReel`.

                          Opacity is derived from a single boolean and written by React
                          alone — nothing touches these styles imperatively, so the two can
                          never both be visible and a re-render can never clobber a fade.

                          `poster` is for the cold start only: before either element has a
                          frame there is genuinely nothing to show, and a lesson's cover is
                          already being fetched by the POPULAR LESSONS row below. Once a
                          frame exists the browser stops consulting it. */}
                      {[reelARef, reelBRef].map((ref, i) => (
                        <video
                          key={i}
                          ref={ref}
                          poster={demoPosterSrc(activeDemoVideo) || undefined}
                          muted
                          playsInline
                          preload="auto"
                          aria-hidden={((i === 0) === frontIsA) ? undefined : "true"}
                          className="absolute inset-0 w-full h-full object-contain
                                     bg-black pointer-events-none"
                          style={{
                            opacity: ((i === 0) === frontIsA) ? 1 : 0,
                            transition: `opacity ${REEL_CROSSFADE_MS}ms linear`,
                          }}
                        />
                      ))}
                      {/* Mute/unmute indicator */}
                      <div
                        className="absolute bottom-3 right-3 z-20 p-2 rounded-full
                                    bg-black/50 text-white/80 hover:bg-black/70 transition-colors"
                      >
                        {demoMuted ? (
                          <VolumeX className="w-4 h-4" />
                        ) : (
                          <Volume2 className="w-4 h-4" />
                        )}
                      </div>
                    </div>
                    <div className="bg-white px-4 py-3 border-t border-gray-100">
                      <p className="text-gray-800 text-sm font-medium truncate">
                        {truncateTitle(
                          playingDemoId
                            ? popularLessonVideos.find(
                                (v) =>
                                  (v.job_id || v.video_id) === playingDemoId,
                              )
                            : activeDemoVideo,
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* end grid wrapper */}
          </div>
          {/* end max-w container */}

          {/* Popular lessons. NOT hidden on mobile any more.
              It used to be `hidden md:block`, which left the phone home with no content block
              at all: the hero reserves min-h-screen, the composer and chips fill ~412px of it,
              and the remaining ~440px was white — measured 912px of dead space down the page,
              more than a whole phone screen. The row is the thing that was meant to fill it.
              The reason given for hiding it was the fall animation during inline playback, but
              that is driven by `popularHiding` and works the same at any width. */}
          <div
            className="block self-stretch -mx-4 sm:-mx-6"
            style={{
              // Full-bleed BOTH ways: these cancel the parent's sm:px-6 gutter so this box
              // spans the entire main content area, edge to edge. The small leading inset
              // lives on the SCROLLER's padding instead (see .home-popular-track) — put it
              // here and cards would clip at the inset rather than at the screen edge.
              //
              // Deliberately not the `calc(50% - 50vw)` full-bleed idiom: <main> is padded by
              // --magi-sidebar, so the content box is not centred in the viewport and any vw
              // form overshoots the window edge by half the rail, adding a horizontal
              // scrollbar. Staying in % keeps it exact at both rail states.
              maxHeight: popularHiding ? 0 : '600px',
              overflow: 'hidden',
              pointerEvents: popularHiding ? 'none' : 'auto',
              transition: popularHiding
                ? (hasPlayedFallRef.current ? 'none' : 'max-height 400ms ease-in 1800ms')
                : 'max-height 400ms ease-out',
            }}
          >
            {sampleVideos.length > 1 && (
              /* Pulled UP toward the composer and player above it — the row sat lower than
                 the space between them warranted. */
              <div style={{ marginTop: expandedForm ? '2.75rem' : '1rem', transition: 'margin-top 600ms cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <p
                  className="text-gray-700 text-[13px] font-bold uppercase tracking-[0.14em] mb-3.5 home-popular-label"
                  style={{
                    animation: popularHiding && !hasPlayedFallRef.current
                      ? 'popular-label-fall 500ms cubic-bezier(0.4, 0, 1, 1) 0ms forwards'
                      : 'none',
                  }}
                >
                  {t('home_popular')}
                </p>
                {/* Both gutters are gone, so the row reaches the window edge at either
                    end and each arrow sits OVER its edge card rather than beside it. That is
                    the trade for a full-bleed row, and it is now taken symmetrically: the
                    left arrow used to have a 3.5rem lane to itself because the row started on
                    the text column, and the row does not start there any more. */}
                <div className="relative">
                  {/* Left arrow — only shown when there's content to scroll to */}
                  {canScrollLeft && (
                    <button
                      onClick={() => scrollRow("left")}
                      style={{ width: "44px" }}
                      className="absolute inset-y-0 z-20 hidden md:flex items-center justify-center home-popular-arrow-left
                                 text-gray-400 hover:text-gray-700 transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5 text-gray-500" />
                    </button>
                  )}
                  {/* Right arrow — overlaps 8px into thumbnails to cover rounded corners */}
                  <button
                    onClick={() => scrollRow("right")}
                    style={{ width: "44px" }}
                    className={`absolute right-0 inset-y-0 z-20 hidden md:flex items-center justify-center
                               text-gray-400 hover:text-gray-700 transition-colors ${canScrollRight ? "" : "opacity-25 cursor-default"}`}
                  >
                    <ChevronRight className="w-5 h-5 text-gray-500" />
                  </button>
                  <div
                    ref={scrollRowRef}
                    onScroll={updateScrollArrows}
                    className="flex gap-4 overflow-x-auto py-2.5 home-popular-track"
                    style={{
                      scrollbarWidth: "none",
                      msOverflowStyle: "none",
                      WebkitOverflowScrolling: "touch",
                    }}
                  >
                    {popularLessonVideos.map((video, index) => {
                      const videoId = video.job_id || video.video_id;
                      // The popular row is EAGER for every card. This is the shop window —
                      // the row people judge the product by — and a card that paints its
                      // title over an empty box because the image is still deferred reads as
                      // broken, not as thrifty. Scrolling the row sideways must never reveal
                      // a blank. The row is a fixed ~10 cards, so the ceiling is bounded;
                      // deferral belongs on unbounded lists (the Workspace grid), not here.
                      // EAGER_POPULAR_THUMBNAILS now orders the queue rather than gating it.
                      const hiding = popularHiding;
                      const stagger = Math.min(index, 8) * 100;
                      return (
                        <div
                          key={videoId}
                          className="group cursor-pointer flex-shrink-0 w-56 sm:w-72 lg:w-80"
                          style={{
                            transformOrigin: 'top center',
                            animation: hiding && !hasPlayedFallRef.current
                              ? `popular-fall 600ms cubic-bezier(0.4, 0, 1, 1) ${stagger}ms forwards`
                              : 'none',
                          }}
                          onClick={() => {
                            /* A PHONE OPENS THE LESSON. Everything below this line is the
                               desktop demo: it types the title into the composer, streams the
                               video into a player on this page and scrolls to it. That is a
                               good gesture with a mouse and the wrong one on a phone, where it
                               builds a small inset player on a page that has just moved under
                               the user's thumb — so on a phone the row's cards did nothing you
                               would recognise as opening a video. */
                            if (isPhone) {
                              navigate(`/video/${videoId}`);
                              return;
                            }
                            const title = (
                              video.topic ||
                              video.title ||
                              ""
                            ).slice(0, 60);
                            setDemoActive(true);
                            setOverrideTitle(title);
                            setTypedText("");
                            setTypePhase("typing");
                            /* Naming it is the whole job. `currentPreviewId` derives from
                               `playingDemoId`, and the reel loads it into the hidden element
                               and swaps once it has a frame — the current clip keeps playing
                               until then rather than cutting out. */
                            setPlayingDemoId(videoId);
                            setExpandedForm(false);

                            setTimeout(() => smoothScrollTo(demoPlayerRef, 600, 'center'), 100);
                          }}
                        >
                          {/* Thick white bezel + big radius: the card reads as a screen set into a housing
                              rather than a flat tile, which is what makes the row feel like a
                              wall of little televisions. The border is the SAME white as the
                              page surfaces, so the bezel reads as material, not as an outline. */}
                          {/* The black keyline is a real PADDED ELEMENT, not a box-shadow.
                              A shadow paints outside the element's box, and this row is a
                              horizontal scroller — once it scrolls, the card sits flush against
                              the content-box edge and anything drawn outside is clipped. Track
                              padding cannot fix that, because padding scrolls away with the
                              content. As a 1.5px black background behind a white-bordered
                              child, the keyline is INSIDE the layout box and can never clip. */}
                          <div
                            className="rounded-[23.5px] bg-[#0f172a] p-[1.5px]"
                            style={{ boxShadow: "0 5px 16px rgba(15,23,42,0.20)" }}
                          >
                          <div
                            className="aspect-video rounded-[22px] overflow-hidden bg-black relative border-[5px] border-white"
                          >
                            <VideoThumbnail
                              video={video}
                              alt={video.topic || video.title}
                              className="w-full h-full object-cover"
                              /* Always mounted — nothing in this row is ever deferred. But
                                 loading and PRIORITY are separate decisions, and collapsing
                                 them was a mistake: making every card eager also made every
                                 card fetchPriority="high", so ten large JPEGs raced each
                                 other and the four you can actually see lost ground to six
                                 you cannot. Eager for all, high priority for the ones on
                                 screen — the rest still load, just behind them. */
                              loading="eager"
                              fetchPriority={index < EAGER_POPULAR_THUMBNAILS ? "high" : "low"}
                            />
                            {/* z-30 on all three: VideoThumbnail renders its <img> as
                                `relative z-20` inside a wrapper whose z-index is auto, so the
                                image joins THIS stacking context and paints over anything
                                below 20 — which is why the hover play icon never appeared
                                either. */}
                            <div
                              className="absolute inset-0 z-30 pointer-events-none"
                              style={{ background: TITLE_SCRIM }}
                            />
                            {/* Padding on the WRAPPER, never on the clamped <p> — see
                                CLAMP_2_LINES. Clamping by LINES rather than truncating at 60
                                characters also matches Explore, which shows these same videos. */}
                            <div className="absolute bottom-0 left-0 right-0 z-30 p-2.5 pointer-events-none">
                              <p
                                className="text-white text-sm sm:text-base font-bold leading-snug drop-shadow [&_.katex]:text-[0.9em]"
                                style={{ fontFamily: ROW_TITLE_FONT, ...CLAMP_2_LINES }}
                                dangerouslySetInnerHTML={{
                                  __html: safeRenderLatex(video?.title || video?.topic || ""),
                                }}
                              />
                            </div>
                          </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                {/* end relative wrapper for arrows + scroll row */}
              </div>
            )}
          </div>
          {/* end Popular lessons full-width container */}

          {/* Premium strip. Below the popular row so it never sits between the composer and
              the lessons — and hidden entirely for people who already pay, since the whole
              point is upgrading. Also hidden mid-generation, where the page is about the
              video being made, not about selling. */}
          {/* Shown to subscribers as well — deliberate. It doubles as a reminder of
              what the plan includes, and gating it made the strip invisible to the
              people most able to review it. Still hidden mid-generation, where the
              page is about the video being made. */}
          {!isGeneratingMode && (
            /* Square here, wide on Explore. A full-width bar under the lesson row reads as a
               page footer and gets scrolled past; a panel the size of a card sits in the
               content and gets looked at. Capped rather than stretched. */
            /* 2x2 on the page's own 1100px column, so it lines up with the composer row
               rather than sitting inset from everything else. Cards are WIDER THAN TALL:
               four squares this size stacked two-deep ran ~770px and pushed the block past
               the fold, which is what made it feel oversized.

               SEAMS ARE NO LONGER ALL EQUAL, and that is the point. They used to be: 320px
               above this block, 320 below, 320 between Tips and Instagram. Measured on a
               1440x732 laptop that is 44% of the viewport three times over, so each transition
               was a full screen of empty page and read as the page having ended. Worse, equal
               seams everywhere meant nothing grouped — this quadrant belongs with the composer
               and the popular row far more than Tips does, but the rhythm denied it.
               Now 128 above (these belong together), 192 below (the one real topic change, so
               it gets the widest seam), 160 from Tips to Instagram. */
            <div className="w-full max-w-2xl lg:max-w-6xl mt-12 md:mt-32">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <HomeShortcuts />
                <PremiumBanner
                  variant="square"
                  onClick={() => window.dispatchEvent(new Event("magi:open-subscribe"))}
                />
              </div>
            </div>
          )}

          {/* Inline video player — shown when a completed card is clicked */}
          {inlineVideo && (
            <div ref={inlinePlayerRef} className="w-full max-w-4xl mt-32 scroll-mt-20">
              <div className="rounded-2xl overflow-hidden bg-black shadow-lg">
                <GatedVideoPlayer
                  videoId={inlineVideo.videoId}
                  title={inlineVideo.title}
                  onFollowUp={user && generateUnified ? (text) => handleInlineFollowUp(text) : undefined}
                />
              </div>
              <p className="text-gray-900 font-semibold text-lg truncate mt-3">
                {inlineVideo.title}
              </p>
            </div>
          )}
        </div>
        {/* end hero section */}

        {/* ── Section 1: Product Showcase (commented out for testing) ──
        <FadeInSection className="pt-32 pb-20 px-6">
          <div className="max-w-5xl mx-auto">
            <p className="text-xs uppercase tracking-[0.15em] text-gray-400 font-semibold mb-3">
              {t('home_from_community')}
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 tracking-tight mb-10">
              {t('home_community_sub')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {popularLessonVideos.slice(0, 6).map((video) => {
                const videoId = video.job_id || video.video_id;
                return (
                  <div
                    key={videoId}
                    className="group cursor-pointer"
                    onClick={() => setLightboxVideo(video)}
                  >
                    <div className="aspect-video rounded-xl overflow-hidden bg-black relative shadow-sm hover:shadow-lg transition-all duration-300 group-hover:scale-[1.02]">
                      <VideoThumbnail
                        video={video}
                        alt={video.topic || video.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <Play className="w-8 h-8 text-white opacity-0 group-hover:opacity-90 transition-opacity drop-shadow-lg" fill="white" />
                      </div>
                    </div>
                    <p className="text-sm font-medium text-gray-700 mt-3 truncate group-hover:text-gray-900 transition-colors">
                      {truncateTitle(video)}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </FadeInSection>
        */}

        {/* ── Section 2: Tips & Tricks ── */}
        {/* Both paddings are half of a seam, not a whole one — the seam above is this 160 plus
              the 32 of the hero's own pb-8 (192 total), and the seam below is this 80 meeting
              the Instagram block's 80. See the quadrant above for why the three seams are
              deliberately unequal now (128 / 192 / 160) rather than a flat 320.
              MOBILE GETS ITS OWN STEP. pt-32/pb-32 is 128px of white at each end, which on a
              393x852 phone is most of a screen spent on nothing: measured, this section's two
              seams were 208px and 280px. A seam should be proportional to the viewport it is
              seen in, and the desktop rhythm above is preserved from md. */}
          <div className="pt-14 pb-14 md:pt-40 md:pb-20 px-6">
          <div className="max-w-5xl mx-auto">
            <p className="text-xs uppercase tracking-[0.15em] text-gray-400 font-semibold mb-3">
              {t('home_tips_label')}
            </p>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 tracking-tight mb-6 md:mb-12">
              {t('home_tips_title')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                { title: t('home_tip1_title'), desc: t('home_tip1_desc'), example: t('home_tip1_example') },
                { title: t('home_tip2_title'), desc: t('home_tip2_desc'), example: t('home_tip2_example') },
                { title: t('home_tip3_title'), desc: t('home_tip3_desc'), example: t('home_tip3_example') },
              ].map((tip) => (
                <div key={tip.title} className="bg-white rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.04),0_6px_24px_rgba(0,0,0,0.06)] p-6 border-t-[3px] border-[#635BFF]">
                  <p className="text-gray-900 font-bold text-lg mb-2">{tip.title}</p>
                  <p className="text-base text-gray-600 leading-relaxed mb-5">{tip.desc}</p>
                  <div className="bg-[#F8FAFC] rounded-xl px-4 py-3 border border-gray-100">
                    <p className="text-xs text-gray-400 mb-1">{t('home_tip_try')}</p>
                    <p className="text-base text-gray-800 font-medium">"{tip.example}"</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Section 3: Instagram Showcase + CTA ── */}
        {/* py-32 is 128px at each end. Stacked against the Tips section's own bottom padding
            that made a 208px seam on a phone — a quarter of the viewport spent on nothing
            between two blocks that are already visually distinct.
            TOP AND BOTTOM ARE SPLIT on desktop: pt-20 is half of the 160px seam shared with
            Tips, while pb-40 stays 160 because it is the end of the page, not a seam. Folding
            them back into one py- value is what would silently change the page's foot. */}
        <div className="py-12 md:pt-20 md:pb-40 px-6">
          {/* 88rem HERE AND NOWHERE ELSE ON THE PAGE, because three 4:5 cards side by side
              is the one block whose content is genuinely wide. The geometry is unforgiving:
              at a given overlap the fan needs 2.24 card widths, so inside the page's usual
              6xl column the cards could only have grown from 300 to about 310 — which is not
              "bigger", it is a rounding error. 1408px buys the fan 907px and still leaves the
              text 453px, which is more than its own `max-w-md` of 448, so the text column
              gives up nothing at all. There is room for it: at a 1440px window the zoomed
              content area is 1682px, so even this leaves a wide margin. It is a max, so
              narrow screens simply use what they have.

              AND IT STAYS ONE COLUMN UNTIL lg. The two-column split used to start at md,
              where the 1.2fr cell is only 367px at the breakpoint's own start — narrower
              than the single column a 767px phone gets, so the cards had to SHRINK on the
              way up from sm to md. Stacking until there is room for two columns means the
              fan keeps its 300px cards all the way through the tablet range. */}
          <div className="max-w-[88rem] mx-auto grid grid-cols-1 lg:grid-cols-[2fr_1fr]
                          gap-8 lg:gap-12 items-center">
            {/* ── Left side — reel cards fanned ACROSS, not stacked DOWN ──
             *
             *  They were a vertical column: three 300px cards at `marginTop: -20%`, which in
             *  a ~594px cell used half the width and ran over 900px tall. A tall narrow
             *  block beside a short one is what left the right-hand half of the section
             *  empty.
             *
             *  Now they overlap horizontally at 38% of a card's width, so three cards span
             *  2.24 cards instead of stacking, and the card grows from 300 to 400.
             *
             *  38% AND NOT MORE. 48% was tried first and fits a narrower column, but it
             *  leaves each card showing barely half of itself: the middle cover's title read
             *  "ON IN HISTORY / ATIVE IQ" and two of the three stat lines were behind the
             *  card in front. The overlap has to clear the covers' own content, not just
             *  look like a fan.
             *
             *  ONE SOURCE FOR THE SIZE: `--reel-w` is the card width and the overlap is
             *  `calc(var(--reel-w) * -0.38)`, so there is no second number to keep in step.
             *
             *  EACH VALUE IS WHAT FITS THE NARROWEST VIEWPORT IN ITS RANGE, not a taste
             *  ramp, because the fan needs 2.24 card widths and the cell is fluid inside
             *  each breakpoint. md starts at a 672px column (W <= 300) and lg at a 743px
             *  cell (W <= 332); xl is the first range where the column hits its 1408px
             *  ceiling, the cell is a full 907px, and the cards can have 400.
             *
             *  THE sm AND BASE ROWS ARE GONE, and so is the fan below md — see the note on
             *  the two containers underneath. 115px cards overlapping by 38% is not a fan,
             *  it is three slivers.
             *
             *  A PERCENTAGE WAS TRIED HERE AND DOES NOT WORK. `--reel-w: 44%` is emitted
             *  correctly and still collapses: against this box the percentage is indefinite,
             *  so `width` falls back to auto, `aspect-ratio` then sizes each card off its
             *  own image's intrinsic size, and the row renders as three different tiny
             *  rectangles with no overlap (the negative margin resolves to nothing too).
             *  The table is longer but it is the thing that renders. */}
            {/* TWO ARRANGEMENTS OF THE SAME THREE CARDS, SPLIT AT md.
             *
             *  The fan below was written for the wide end and sized down from there, which
             *  left a phone with three 115px cards overlapping by 38% — barely a thumbnail
             *  each, and the thing it replaced (a stacked column of 260px cards) read far
             *  better at that width. So the fan starts at md, where the cards are already
             *  300px and the overlap has room to work, and below it the column is back.
             *
             *  ONE CARD, TWO CONTAINERS. `ReelCard` holds the cover, the gradient and the
             *  stat line; only the ARRANGEMENT differs, so only the wrapper's geometry is
             *  written twice. Duplicating the card markup instead is how the two copies come
             *  to disagree about the overlay six months from now.
             *
             *  Rendered both ways and hidden with `md:hidden` / `hidden md:flex` rather than
             *  chosen in JS: a media-query hook would pick a layout on the client after
             *  mount, so the first paint would be the wrong one and then jump. The covers
             *  are the same three URLs either way and both copies are `loading="lazy"`, so
             *  the browser fetches each once. */}

            {/* Phones — the stacked column, as it was before the fan. */}
            <div className="flex flex-col items-center p-6 md:hidden">
              {INSTAGRAM_REELS.map((item, index) => (
                <ReelCard
                  key={item.id}
                  item={item}
                  className="w-[260px] sm:w-[280px]"
                  style={{
                    transform: `rotate(${[-3, 1, -2][index]}deg) translateX(${[0, 30, -10][index]}px)`,
                    // Against the CONTAINER's width, which is what a column wants.
                    marginTop: index === 0 ? '0' : '-20%',
                    // First card on top: a column overlapping downward should show each
                    // card's TOP edge, where the cover puts its title.
                    zIndex: INSTAGRAM_REELS.length - index,
                  }}
                />
              ))}
            </div>

            {/* md and up — the fan. */}
            <div
              ref={cardStackRef}
              className="hidden md:flex flex-row items-center justify-center lg:justify-start
                         p-4 lg:p-2
                         [--reel-w:300px] lg:[--reel-w:320px] xl:[--reel-w:400px]"
            >
              {INSTAGRAM_REELS.map((item, index) => (
                <ReelCard
                  key={item.id}
                  item={item}
                  className="flex-shrink-0"
                  style={{
                    width: 'var(--reel-w)',
                    transform: `rotate(${[-5, 2, -4][index]}deg) translateY(${[30, -32, 20][index]}px)`,
                    // Percentage margins resolve against the CONTAINING BLOCK's width, not the
                    // element's — which is why this is calc() off the card's own property.
                    marginLeft: index === 0 ? 0 : 'calc(var(--reel-w) * -0.38)',
                    // LATER CARDS ON TOP: a fan spreading rightward wants each card to cover
                    // the one before it, so what stays visible is every card's left edge —
                    // where the covers put their titles and all three put their counts.
                    zIndex: index,
                  }}
                />
              ))}
            </div>

            {/* Right side — Instagram description + CTA */}
            <div className="flex flex-col items-start">
              <p className="text-xs uppercase tracking-[0.15em] text-gray-400 font-semibold mb-4">
                {t('home_proven_social')}
              </p>
              <a
                href="https://www.instagram.com/magi.academy/"
                target="_blank"
                rel="noopener noreferrer"
                className="group"
              >
                <p className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight group-hover:text-[#635BFF] transition-colors mb-3">
                  @magi.academy
                </p>
              </a>
              <p className="text-lg text-gray-500 leading-relaxed mb-6 max-w-md">
                {t('home_ig_desc_1')}<span className="text-gray-900 font-semibold">{t('home_ig_followers')}</span>{t('home_ig_desc_2')}
              </p>
              <a
                href="https://www.instagram.com/magi.academy/"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 text-sm text-[#635BFF] font-medium hover:text-[#5851e0] transition-colors mb-10"
              >
                <Instagram className="w-4 h-4" />
                {t('home_view_profile')}
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
              <button
                onClick={() => setShowSubscriptionModal(true)}
                className="bg-[#635BFF] hover:bg-[#5851e0] text-white px-8 py-4 rounded-xl text-lg font-semibold transition-colors"
              >
                {t('home_subscribe_now')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {showSubscriptionModal && (
        <SubscriptionModal
          onClose={() => setShowSubscriptionModal(false)}
          user={user}
        />
      )}

      {/* Same flow the Workspace composer opens, centred rather than docked — Home's
          composer is a hero card in the middle of the page, not a bar pinned to the bottom
          for the modal to grow out of. */}

      {/* Video Lightbox — opens when clicking a video in "See what students are creating" */}
      {lightboxVideo && (
        <VideoLightboxModal
          video={lightboxVideo}
          onClose={() => setLightboxVideo(null)}
        />
      )}
    </>
  );
};

// --- VideoLightboxModal: cinematic video overlay ---
const VideoLightboxModal = ({ video, onClose }) => {
  const [isClosing, setIsClosing] = useState(false);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  // Escape key
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // View tracking
  useEffect(() => {
    const id = video.job_id || video.video_id;
    if (id) apiService.trackVideoView(id).catch(() => {});
  }, [video]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => onClose(), 200);
  };

  const videoId = video.job_id || video.video_id;

  return (
    <>
      <style>{`
        @keyframes lightboxFadeIn {
          from { opacity: 0; transform: scale(0.96) translateY(16px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>

      {/* Overlay */}
      <div
        onClick={handleClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.85)",
          zIndex: 9000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          opacity: isClosing ? 0 : 1,
          transition: "opacity 200ms ease",
        }}
      >
        {/* Close button */}
        <button
          onClick={(e) => { e.stopPropagation(); handleClose(); }}
          style={{
            position: "fixed",
            top: 20,
            right: 20,
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.1)",
            border: "none",
            color: "white",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9001,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.2)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.1)"; }}
        >
          <X size={20} />
        </button>

        {/* Content container */}
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            maxWidth: 900,
            width: "90vw",
            cursor: "default",
            animation: isClosing ? "none" : "lightboxFadeIn 300ms ease-out",
            opacity: isClosing ? 0 : 1,
            transform: isClosing ? "scale(0.96)" : "scale(1)",
            transition: isClosing ? "opacity 200ms, transform 200ms" : "none",
          }}
        >
          {/* Video player */}
          <div style={{ borderRadius: 12, overflow: "hidden", boxShadow: "0 25px 50px rgba(0,0,0,0.5)" }}>
            <GatedVideoPlayer
              videoId={video.job_id || video.video_id}
            />
          </div>

          {/* Title */}
          <p style={{
            color: "white",
            fontSize: 18,
            fontWeight: 600,
            marginTop: 16,
            fontFamily: "system-ui, sans-serif",
          }}>
            {video.topic || video.title || "Untitled"}
          </p>

          {/* Description */}
          {video.description && (
            <p style={{
              color: "#9CA3AF",
              fontSize: 14,
              marginTop: 6,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}>
              {video.description}
            </p>
          )}
        </div>
      </div>
    </>
  );
};

export default HomePage;
